<template>
  <header
    class="sticky top-0 z-50 w-full bg-white border-b-2 border-charcoal-blue"
    role="banner"
  >
    <div class="max-w-[1400px] mx-auto h-20 flex items-center justify-between px-4 sm:px-6">
      <div class="flex min-w-0 flex-1 items-center pr-3 md:pr-0">
        <span
          class="heading-utilitarian block max-w-full min-w-0 truncate text-2xl font-extrabold leading-tight tracking-tight text-charcoal-blue md:text-3xl"
          :title="name || 'Restaurant'"
        >
          {{ name || 'Restaurant' }}
        </span>
      </div>
      <nav class="hidden md:flex items-center gap-1">
        <a class="heading-utilitarian text-sm px-6 py-2 min-h-[44px] flex items-center hover:bg-concrete-gray transition-colors focus:outline-none rms-t2-accent-ring" href="#menu">{{ navLabel || viewListLabel || $t('public.t2MenuNav') }}</a>
        <a v-if="feedbackAvailable" class="heading-utilitarian text-sm px-6 py-2 min-h-[44px] flex items-center hover:bg-concrete-gray transition-colors focus:outline-none rms-t2-accent-ring" href="#reviews">{{ $t('public.t2ReviewsNav') }}</a>
        <div v-else-if="isOwnerViewer && ownerViewMode" class="py-2 px-3 rounded border border-dashed border-charcoal-blue/40 bg-charcoal-blue/5 max-w-[140px]">
          <p class="text-xs font-mono text-charcoal-blue/70 break-words whitespace-normal">{{ $t('public.ownerNavReviewsUnavailable') }}</p>
        </div>
        <a class="heading-utilitarian text-sm px-6 py-2 min-h-[44px] flex items-center hover:bg-concrete-gray transition-colors focus:outline-none rms-t2-accent-ring" href="#contact">{{ $t('public.t2ContactNav') }}</a>
        <PublicReservationTrigger
          v-if="reservationAvailable"
          :slug="slug"
          template="template-2"
          :primary-color="primaryColor"
          :button-label="reserveButtonLabel"
          :modal-title="reserveModalHeading"
          :pax-label="reservePaxLabel"
        >
          <template #button="{ openModal }">
            <button
              type="button"
              class="ml-4 text-white px-8 py-3 heading-utilitarian text-sm font-bold border-2 border-charcoal-blue hover:translate-x-1 hover:translate-y-1 hover:shadow-none shadow-[4px_4px_0px_0px_rgba(30,41,59,1)] transition-all min-h-[44px] flex items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-charcoal-blue focus-visible:ring-offset-2"
              :class="primaryColor ? '' : 'bg-oxidized-copper'"
              :style="primaryColor ? { backgroundColor: primaryColor } : undefined"
              :aria-label="reserveButtonLabel"
              data-testid="reservation-open-modal"
              @click="openModal"
            >
              {{ reserveButtonLabel }}
            </button>
          </template>
        </PublicReservationTrigger>
        <div
          v-else-if="isOwnerViewer && ownerViewMode"
          class="ml-4 py-2 px-3 rounded border border-dashed border-charcoal-blue/40 bg-charcoal-blue/5 max-w-[140px]"
        >
          <p class="text-xs font-mono text-charcoal-blue/70 break-words whitespace-normal">{{ $t('public.ownerNavBookingUnavailable') }}</p>
        </div>
        <LanguageDropdown
          :languages="languages"
          :current-locale="currentLocale"
          class="rms-language-dropdown--t2 text-charcoal-blue border-charcoal-blue"
          @select-locale="$emit('select-locale', $event)"
        />
      </nav>
      <div class="flex shrink-0 items-center gap-2 md:hidden">
        <LanguageDropdown
          :languages="languages"
          :current-locale="currentLocale"
          class="rms-language-dropdown--t2 rms-language-dropdown--header-mobile text-charcoal-blue border-charcoal-blue"
          @select-locale="$emit('select-locale', $event)"
        />
        <button
          type="button"
          class="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-charcoal-blue rounded focus:outline-none rms-t2-accent-ring"
          :aria-label="mobileNavOpen ? $t('app.close') : (viewListAriaLabel || $t('public.t2MenuNav'))"
          :aria-expanded="mobileNavOpen"
          data-testid="header-mobile-menu-btn"
          @click="mobileNavOpen = !mobileNavOpen"
        >
          <span class="material-symbols-outlined text-4xl">{{ mobileNavOpen ? 'close' : 'menu' }}</span>
        </button>
      </div>
    </div>
    <!-- Mobile nav panel -->
    <Transition name="rms-mobile-nav">
      <div
        v-if="mobileNavOpen"
        class="fixed inset-0 top-20 z-40 md:hidden"
        role="dialog"
        aria-modal="true"
        aria-label="Navigation"
      >
        <div class="absolute inset-0 bg-charcoal-blue/40" aria-hidden="true" @click="mobileNavOpen = false" />
        <nav class="absolute left-0 right-0 top-0 bg-white border-b-2 border-charcoal-blue shadow-lg flex flex-col py-4 px-4 gap-1" aria-label="Main">
          <a class="heading-utilitarian text-base font-semibold min-h-[44px] flex items-center px-4 rounded-lg hover:bg-concrete-gray text-charcoal-blue focus:outline-none rms-t2-accent-ring" href="#menu" @click="mobileNavOpen = false">{{ navLabel || viewListLabel || $t('public.t2MenuNav') }}</a>
          <a v-if="feedbackAvailable" class="heading-utilitarian text-base font-semibold min-h-[44px] flex items-center px-4 rounded-lg hover:bg-concrete-gray text-charcoal-blue focus:outline-none rms-t2-accent-ring" href="#reviews" @click="mobileNavOpen = false">{{ $t('public.t2ReviewsNav') }}</a>
          <div v-else-if="isOwnerViewer && ownerViewMode" class="py-3 px-3 rounded border border-dashed border-charcoal-blue/40 bg-charcoal-blue/5" @click="mobileNavOpen = false">
            <p class="text-xs font-mono text-charcoal-blue/70">{{ $t('public.ownerNavReviewsUnavailable') }}</p>
          </div>
          <a class="heading-utilitarian text-base font-semibold min-h-[44px] flex items-center px-4 rounded-lg hover:bg-concrete-gray text-charcoal-blue focus:outline-none rms-t2-accent-ring" href="#contact" @click="mobileNavOpen = false">{{ $t('public.t2ContactNav') }}</a>
          <PublicReservationTrigger
            v-if="reservationAvailable"
            :slug="slug"
            template="template-2"
            :primary-color="primaryColor"
            :button-label="reserveButtonLabel"
            :modal-title="reserveModalHeading"
            :pax-label="reservePaxLabel"
          >
            <template #button="{ openModal }">
              <button
                type="button"
                class="w-full text-left heading-utilitarian text-base font-bold min-h-[44px] flex items-center px-4 rounded-lg text-white border-2 border-charcoal-blue focus:outline-none focus-visible:ring-2 focus-visible:ring-charcoal-blue focus-visible:ring-offset-2"
                :class="primaryColor ? '' : 'bg-oxidized-copper'"
                :style="primaryColor ? { backgroundColor: primaryColor } : undefined"
                :aria-label="reserveButtonLabel"
                data-testid="reservation-open-modal-mobile"
                @click="openModal()"
              >
                {{ reserveButtonLabel }}
              </button>
            </template>
          </PublicReservationTrigger>
          <div
            v-else-if="isOwnerViewer && ownerViewMode"
            class="py-3 px-3 rounded border border-dashed border-charcoal-blue/40 bg-charcoal-blue/5"
            @click="mobileNavOpen = false"
          >
            <p class="text-xs font-mono text-charcoal-blue/70">{{ $t('public.ownerNavBookingUnavailable') }}</p>
          </div>
        </nav>
      </div>
    </Transition>
    <div
      v-if="showOwnerNeedsDataNotice"
      class="max-w-[1400px] mx-auto px-4 sm:px-6 py-3"
      role="status"
      aria-live="polite"
    >
      <div class="py-4 px-4 rounded border border-dashed border-charcoal-blue/40 bg-charcoal-blue/5">
        <p class="text-sm font-mono text-charcoal-blue/70">{{ $t('public.ownerNeedsDataIntro') }}</p>
        <a
          v-if="ownerAdminUrl"
          :href="ownerAdminUrl"
          class="mt-2 inline-flex items-center text-sm font-mono text-charcoal-blue/70 underline underline-offset-2 hover:text-charcoal-blue focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-charcoal-blue/40 focus-visible:ring-offset-1 rounded min-h-[44px]"
          :aria-label="$t('public.openAdminPage')"
        >
          {{ $t('public.openAdminPage') }}
        </a>
      </div>
    </div>
  </header>
</template>

<script setup>
import { ref, computed } from 'vue'
import LanguageDropdown from '@/components/public/LanguageDropdown.vue'
import PublicReservationTrigger from '@/components/public/PublicReservationTrigger.vue'

const mobileNavOpen = ref(false)

const props = defineProps({
  name: { type: String, default: '' },
  viewer: { type: Object, default: () => ({ is_owner: false, owner_admin_url: null, needs_data: false }) },
  /** When false, owner-only notice is hidden (e.g. owner toggled "view as customer"). */
  ownerViewMode: { type: Boolean, default: true },
  languages: { type: Array, default: () => [] },
  currentLocale: { type: String, default: '' },
  reservationAvailable: { type: Boolean, default: false },
  slug: { type: String, default: '' },
  primaryColor: { type: String, default: null },
  viewListLabel: { type: String, default: '' },
  navLabel: { type: String, default: '' },
  viewListAriaLabel: { type: String, default: '' },
  reserveButtonLabel: { type: String, default: '' },
  reserveModalHeading: { type: String, default: '' },
  reservePaxLabel: { type: String, default: '' },
  feedbackAvailable: { type: Boolean, default: false },
})

const isOwnerViewer = computed(() => props.viewer?.is_owner === true)
const ownerAdminUrl = computed(() => {
  const url = props.viewer?.owner_admin_url
  return typeof url === 'string' && url.trim() !== '' ? url : null
})
/** Show "needs data" notice only when owner is viewing and site needs data and not in "view as customer" mode. */
const showOwnerNeedsDataNotice = computed(() =>
  props.ownerViewMode && isOwnerViewer.value && props.viewer?.needs_data === true
)

defineEmits(['select-locale'])
</script>

<style scoped>
.rms-mobile-nav-enter-active,
.rms-mobile-nav-leave-active {
  transition: opacity 0.2s ease;
}
.rms-mobile-nav-enter-active nav,
.rms-mobile-nav-leave-active nav {
  transition: transform 0.2s ease;
}
.rms-mobile-nav-enter-from,
.rms-mobile-nav-leave-to {
  opacity: 0;
}
.rms-mobile-nav-enter-from nav,
.rms-mobile-nav-leave-to nav {
  transform: translateY(-8px);
}
</style>
